/**
 * CR043/CR142/CR201/CR225: Value Starburst Badge
 * 
 * Renders a starburst badge with value percentage.
 * CR142: Uses Asset Studio assets instead of hardcoded SVG.
 * CR201: Renders via <img> instead of dangerouslySetInnerHTML (XSS fix).
 * CR225 Addendum: Visibility-controlled, NOT data-gated.
 *        When showStarburst=true, ALWAYS renders (with fallback label if no payload).
 */

import { starburstStyles } from "@/lib/deals";
import { useAppAssets } from "@/hooks/useAppAssets";
import { useGlobalTheme } from "@/providers/GlobalThemeProvider";
import { useMemo } from "react";
import { SafeAssetImage } from "@/components/ui/SafeAssetImage";
import { getStarburstFallbackLabel } from "@/lib/deals/cardVisibility";

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

// Built-in fallback starburst SVG (used when no Asset Studio assets exist)
const FALLBACK_STARBURST_SVG = `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="fallbackGradient" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#FFD700"/>
      <stop offset="50%" stop-color="#FFC107"/>
      <stop offset="100%" stop-color="#FF9800"/>
    </linearGradient>
    <filter id="fallbackShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.25"/>
    </filter>
  </defs>
  <polygon points="60,5 71,35 105,35 78,57 89,91 60,70 31,91 42,57 15,35 49,35" fill="url(#fallbackGradient)" filter="url(#fallbackShadow)"/>
</svg>`;

interface ValueStarburstBadgeProps {
  /** Numeric value to display (optional - fallback label used if null/0) */
  valuePercent?: number | null;
  size?: "sm" | "md" | "lg";
  /** If true, displays value as a pure percentage without the "+" prefix (for discount deals) */
  isPercent?: boolean;
  /** Override asset ID (for preview/testing) */
  overrideAssetId?: string | null;
  /** CR225: Deal type for fallback label */
  dealType?: string;
  /** CR225: Custom label override (takes precedence over computed value) */
  customLabel?: string;
  /** CR225: Force render even without value (visibility-controlled mode) */
  forceRender?: boolean;
}

export function ValueStarburstBadge({ 
  valuePercent, 
  size = "md", 
  isPercent = false,
  overrideAssetId,
  dealType,
  customLabel,
  forceRender = false,
}: ValueStarburstBadgeProps) {
  // Load assets and theme
  const { assets } = useAppAssets({ type: "starburst", status: "active" });
  const { tokens } = useGlobalTheme();
  
  // Determine which asset to use
  const resolvedAssetId = overrideAssetId ?? tokens.components.starburst.assetId;
  
  // CR142/CR201: Resolve starburst asset with fallback logic
  const resolvedAsset = useMemo(() => {
    if (!assets || assets.length === 0) {
      return null;
    }
    
    // 1. If a specific asset is selected, use it
    if (resolvedAssetId) {
      const selected = assets.find(a => a.id === resolvedAssetId);
      if (selected) return selected;
    }
    
    // 2. Find asset with "default" tag
    const defaultAsset = assets.find(a => 
      a.tags?.some(t => t.toLowerCase() === "default")
    );
    if (defaultAsset) return defaultAsset;
    
    // 3. Use most recently updated active starburst
    const sortedByDate = [...assets].sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    return sortedByDate[0] || null;
  }, [assets, resolvedAssetId]);

  // CR225: Determine what to display
  const hasValue = valuePercent != null && !Number.isNaN(valuePercent) && valuePercent > 0;
  
  // If no value AND not forcing render, don't show
  if (!hasValue && !forceRender && !customLabel) return null;

  // Compute display text
  let displayText: string;
  let displayLabel: string;
  
  if (customLabel) {
    displayText = customLabel;
    displayLabel = "";
  } else if (hasValue) {
    const v = clamp(Math.round(valuePercent!), 0, 99);
    displayText = isPercent ? `${v}%` : `+${v}%`;
    displayLabel = isPercent ? "off" : "value";
  } else {
    // CR225 Addendum: Use fallback label when no payload
    displayText = getStarburstFallbackLabel(dealType);
    displayLabel = "";
  }
  
  // Use centralized styles
  const sizeClass = starburstStyles.sizes[size];
  const textSize = starburstStyles.textSizes[size];

  return (
    <div className="relative flex items-center justify-center" data-testid="value-starburst">
      <div className={`relative ${sizeClass}`}>
        {/* CR201: Starburst rendered via <img> - never dangerouslySetInnerHTML */}
        <SafeAssetImage
          url={resolvedAsset?.url}
          svg={resolvedAsset?.svg || FALLBACK_STARBURST_SVG}
          alt="Value starburst"
          className="absolute inset-0 w-full h-full drop-shadow-lg"
        />
        
        {/* Text content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className={`${textSize.value} font-black text-black drop-shadow-sm leading-none`}>
            {displayText}
          </span>
          {displayLabel && (
            <span 
              className={`${textSize.label} font-semibold uppercase tracking-wider text-black/70 mt-0.5 px-1.5 py-0.5 bg-white/60 rounded-full`}
            >
              {displayLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to get starburst assets for selector UI
 */
export function useStarburstAssets() {
  const { assets, loading } = useAppAssets({ type: "starburst", status: "active" });
  
  const defaultAsset = useMemo(() => {
    if (!assets || assets.length === 0) return null;
    
    // Find asset with "default" tag
    const tagged = assets.find(a => 
      a.tags?.some(t => t.toLowerCase() === "default")
    );
    if (tagged) return tagged;
    
    // Fallback to most recent
    const sorted = [...assets].sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    return sorted[0] || null;
  }, [assets]);
  
  return {
    assets: assets || [],
    defaultAsset,
    hasAssets: (assets?.length || 0) > 0,
    loading,
  };
}
